/**
 * ConversationPrivacyTab
 * Gerencia modo de privacidade da conversa: toggle on/off + lista de usuários autorizados
 */

import React, { useState, useMemo } from 'react';
import type { ConversationResponse } from '@troia-v3/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Switch } from '@/shared/components/ui/Switch';
import { Input } from '@/shared/components/ui/Input';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Icon } from '@/shared/icons';
import { useAuth } from '@/contexts/AuthContext';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { updateConversation } from '../../services/chatService';

interface ConversationPrivacyTabProps {
  conversation: ConversationResponse;
  onClose: () => void;
}

interface UserItem {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

export const ConversationPrivacyTab: React.FC<ConversationPrivacyTabProps> = ({
  conversation,
  onClose,
}) => {
  const { state: authState } = useAuth();
  const currentUserId = authState.user?.id ?? '';

  const queryClient = useQueryClient();

  // Initial state from conversation
  const [privacyEnabled, setPrivacyEnabled] = useState<boolean>(
    conversation.privacy?.enabled ?? false
  );
  const [allowedUserIds, setAllowedUserIds] = useState<string[]>(() => {
    const ids = conversation.privacy?.users ?? [];
    // Always ensure current user is included
    if (currentUserId && !ids.includes(currentUserId)) {
      return [currentUserId, ...ids];
    }
    return ids;
  });
  const [userSearch, setUserSearch] = useState('');

  // Fetch all active users
  const { data: allUsers = [], isLoading: isLoadingUsers } = useUsersDropdown({
    status: 'active',
  });

  const filteredUsers = useMemo(() => {
    const term = userSearch.toLowerCase().trim();
    if (!term) return allUsers as UserItem[];
    return (allUsers as UserItem[]).filter(
      (u) =>
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term)
    );
  }, [allUsers, userSearch]);

  const toggleUser = (userId: string) => {
    if (userId === currentUserId) return; // Cannot remove self
    setAllowedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const mutation = useMutation({
    mutationFn: () =>
      updateConversation(conversation.id, {
        privacy: {
          enabled: privacyEnabled,
          users: allowedUserIds,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversation.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      toast.success('Configurações de privacidade salvas');
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao salvar privacidade');
    },
  });

  const handleSave = () => {
    if (privacyEnabled && allowedUserIds.length === 0) {
      toast.error('Adicione pelo menos 1 usuário ao modo de privacidade');
      return;
    }
    mutation.mutate();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Toggle */}
        <div
          className="p-4 rounded-xl"
          style={{ backgroundColor: '#f8fafc', border: '1px solid #e5e7eb' }}
        >
          <Switch
            label="Modo de privacidade"
            description="Restringe o acesso a esta conversa somente para os usuários selecionados abaixo."
            checked={privacyEnabled}
            onChange={setPrivacyEnabled}
          />
        </div>

        {/* Warning when enabled */}
        {privacyEnabled && (
          <div
            className="flex gap-2 p-3 rounded-lg"
            style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d' }}
          >
            <Icon name="alert-triangle" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
            <p className="text-xs" style={{ color: '#92400e' }}>
              Com o modo de privacidade ativo, apenas os usuários listados abaixo poderão visualizar
              e responder esta conversa. Você (usuário atual) sempre estará incluído e não pode ser
              removido.
            </p>
          </div>
        )}

        {/* User list (only relevant when privacy is enabled) */}
        <div style={{ opacity: privacyEnabled ? 1 : 0.5, pointerEvents: privacyEnabled ? 'auto' : 'none' }}>
          <p className="text-sm font-medium mb-2" style={{ color: '#374151' }}>
            Usuários com acesso
          </p>

          <div className="mb-2">
            <Input
              placeholder="Pesquisar usuário..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          </div>

          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <div
                className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: '#8b5cf6' }}
              />
            </div>
          ) : (
            <div
              className="rounded-xl overflow-hidden"
              style={{ border: '1px solid #e5e7eb', maxHeight: '220px', overflowY: 'auto' }}
            >
              {filteredUsers.length === 0 ? (
                <div className="py-8 flex items-center justify-center">
                  <p className="text-sm" style={{ color: '#94a3b8' }}>
                    Nenhum usuário encontrado
                  </p>
                </div>
              ) : (
                filteredUsers.map((user, idx) => {
                  const isAllowed = allowedUserIds.includes(user.id);
                  const isCurrentUser = user.id === currentUserId;
                  return (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors"
                      style={{
                        backgroundColor: isAllowed ? 'rgba(139,92,246,0.05)' : idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                        borderBottom: idx < filteredUsers.length - 1 ? '1px solid #f1f5f9' : 'none',
                      }}
                      onClick={() => toggleUser(user.id)}
                      onMouseEnter={(e) => {
                        if (!isCurrentUser) {
                          e.currentTarget.style.backgroundColor = '#f5f3ff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isAllowed
                          ? 'rgba(139,92,246,0.05)'
                          : idx % 2 === 0
                          ? '#ffffff'
                          : '#f8fafc';
                      }}
                    >
                      <Avatar className="w-8 h-8 flex-shrink-0" isSquircle>
                        <AvatarImage src={user.avatar} alt={user.name} isSquircle />
                        <AvatarFallback isSquircle>
                          <span className="text-xs font-semibold" style={{ color: '#8b5cf6' }}>
                            {user.name.charAt(0)}
                          </span>
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                          {user.name}
                          {isCurrentUser && (
                            <span className="ml-1 text-xs font-normal" style={{ color: '#94a3b8' }}>
                              (você)
                            </span>
                          )}
                        </p>
                        <p className="text-xs truncate" style={{ color: '#94a3b8' }}>
                          {user.email}
                        </p>
                      </div>
                      <div
                        className="w-5 h-5 rounded flex-shrink-0 flex items-center justify-center"
                        style={{
                          backgroundColor: isAllowed ? '#8b5cf6' : 'transparent',
                          border: `2px solid ${isAllowed ? '#8b5cf6' : '#d1d5db'}`,
                        }}
                      >
                        {isAllowed && (
                          <Icon name="check" className="w-3 h-3" style={{ color: '#ffffff' }} />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {privacyEnabled && allowedUserIds.length > 0 && (
            <p className="mt-2 text-xs" style={{ color: '#94a3b8' }}>
              {allowedUserIds.length} usuário{allowedUserIds.length !== 1 ? 's' : ''} com acesso
            </p>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div
        className="flex-shrink-0 flex items-center justify-end gap-3 px-4 py-3 border-t"
        style={{ borderColor: '#e5e7eb', backgroundColor: '#f8fafc' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
          style={{ color: '#64748b' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={mutation.isPending}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
          onMouseEnter={(e) => {
            if (!mutation.isPending) e.currentTarget.style.backgroundColor = '#7c3aed';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#8b5cf6';
          }}
        >
          {mutation.isPending ? (
            <>
              <div
                className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: '#ffffff' }}
              />
              Salvando...
            </>
          ) : (
            <>
              <Icon name="save" className="w-4 h-4" />
              Salvar
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ConversationPrivacyTab;
